import { useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { setDrawerOpen } from 'features/ui/model/uiSlice';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useGetCountriesQuery } from 'features/getCountries/api/getCountriesApi';
import { Controller, useForm } from 'react-hook-form';
import ListItemText from '@mui/material/ListItemText';
import { useGetCitiesQuery } from 'features/getCities/api/getCitiesApi';
import { useGetB2BSalesPointsQuery } from 'features/getB2BConstants/b2bConstants';
import { ImageUpload } from 'shared/ui/ImageUpload/ui/ImageUpload';
import { useGetDirectionsQuery } from 'features/getDirections';
import { useUploadNewsImageMutation } from 'features/uploadNewsImage/uploadNewsImage';
import { useAddBrandMutation, useEditBrandMutation } from 'features/useBrands';
import { DrawerContent } from 'shared/ui/Drawer/DrawerStyles';

type FormDataType = {
  title: string;
  countries: number[];
  countries_value: string[];
  cities: number[];
  cities_value: string[];
  directions: number[];
  directions_value: string[];
  b2b_directions: number[];
  b2b_directions_value: string[];
  b2b_tiers: number[];
  b2b_tiers_value: string[];
  image: string;
  priority: number;
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const BrandFormWidget = ({ refetch, item }: { refetch: any; item?: any }) => {
  const dispatch = useAppDispatch();
  const { isDrawerOpen } = useAppSelector((state) => state.ui);

  const countries = useGetCountriesQuery(null);
  const cities = useGetCitiesQuery({});
  const constants = useGetB2BSalesPointsQuery('constants');
  const directions = useGetDirectionsQuery('directions');
  const [uploadFunc, uploadProps] = useUploadNewsImageMutation();
  const [addBrand] = useAddBrandMutation();
  const [editBrand] = useEditBrandMutation();

  useEffect(() => {
    if (uploadProps.data && uploadProps.isSuccess) {
      // @ts-ignore
      setValue('image', uploadProps?.data.file_path);
    }
  }, [uploadProps.isSuccess]);

  const uploadImage = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    uploadFunc(formData);
  };

  const getDefaultValues = () => {
    if (item) {
      return {
        title: item.title,
        directions: item.directions ? item.directions.map((obj: any) => obj.id) : [],
        directions_value: item.directions ? item.directions.map((obj: any) => obj.title) : [],
        b2b_directions: item.b2b_directions ? item.b2b_directions.map((obj: any) => obj.id) : [],
        b2b_directions_value: item.b2b_directions ? item.b2b_directions.map((obj: any) => obj.title) : [],
        b2b_tiers: item.b2b_tiers ? item.b2b_tiers.map((obj: any) => obj.id) : [],
        b2b_tiers_value: item.b2b_tiers ? item.b2b_tiers.map((obj: any) => obj.title) : [],
        countries: item.countries,
        countries_value: item.countries_value,
        cities: item.cities,
        cities_value: item.cities_value,
        image: item.image || '',
        priority: item.priority,
      };
    }

    return {
      title: '',
      countries: [],
      countries_value: [],
      cities: [],
      cities_value: [],
      directions: [],
      directions_value: [],
      b2b_directions: [],
      b2b_directions_value: [],
      b2b_tiers: [],
      b2b_tiers_value: [],
      image: '',
    };
  };

  const { control, getValues, setValue } = useForm<FormDataType>({ defaultValues: getDefaultValues() });

  const onSendData = async () => {
    const values = getValues();
    const data = item ? await editBrand({ id: item.id, body: values }) : await addBrand({ body: values });
    // @ts-ignore
    if (data && data.data) {
      refetch();
      dispatch(setDrawerOpen(false));
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => dispatch(setDrawerOpen(false))}
    >
      <DrawerContent style={{ width: '800px' }}>
        <div className="header">
          <Typography
            variant="h5"
            sx={{ fontWeight: '600' }}
          >
            {item ? `Изменить бренд #${item.id}` : 'Новый бренд'}
          </Typography>
          <IconButton
            onClick={() => dispatch(setDrawerOpen(false))}
            sx={{ color: '#000' }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <div className="content">
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <TextField
                value={field.value}
                onChange={(e) => field.onChange(e)}
                label="Заголовок"
                fullWidth
                sx={{ marginBottom: '24px' }}
              />
            )}
          />

          <FormControl sx={{ width: '100%' }}>
            <Controller
              name="countries_value"
              control={control}
              rules={{ required: 'Страна обязательна' }}
              render={({ field: { value, onChange } }) => (
                <>
                  <InputLabel id="country-label">Cтраны</InputLabel>
                  <Select
                    labelId="country-label"
                    id="country-multiple-checkbox"
                    multiple
                    sx={{ marginBottom: '24px' }}
                    value={value}
                    onChange={onChange}
                    fullWidth
                    input={<OutlinedInput label="Cтраны" />}
                    MenuProps={MenuProps}
                    renderValue={(selected) => selected?.map((option) => option).join(', ')}
                  >
                    {countries.data ? (
                      countries.data.map((option) => (
                        <MenuItem
                          key={option.id}
                          value={option.name}
                          onClick={() => {
                            const { countries } = getValues();
                            if (countries.includes(option.id)) {
                              const new_values = countries.filter((item) => item !== option.id);
                              setValue('countries', new_values);
                              return;
                            }
                            setValue('countries', [...countries, option.id]);
                          }}
                        >
                          <Checkbox checked={value.indexOf(option.name) > -1} />
                          <ListItemText primary={option.name} />
                        </MenuItem>
                      ))
                    ) : (
                      <div>Loading</div>
                    )}
                  </Select>
                </>
              )}
            />
          </FormControl>

          <FormControl sx={{ width: '100%' }}>
            <Controller
              name="cities_value"
              control={control}
              rules={{ required: 'Город обязателен' }}
              render={({ field: { value, onChange } }) => (
                <>
                  <InputLabel id="city-label">Города</InputLabel>
                  <Select
                    labelId="city-label"
                    id="city-multiple-checkbox"
                    multiple
                    sx={{ marginBottom: '24px' }}
                    value={value}
                    onChange={onChange}
                    fullWidth
                    input={<OutlinedInput label="Города" />}
                    MenuProps={MenuProps}
                    renderValue={(selected) => selected?.map((option) => option).join(', ')}
                  >
                    {cities.data ? (
                      cities.data.map((option) => (
                        <MenuItem
                          key={option.id}
                          value={option.name}
                          onClick={() => {
                            const { cities } = getValues();
                            if (cities.includes(option.id)) {
                              const new_values = cities.filter((item) => item !== option.id);
                              setValue('cities', new_values);
                              return;
                            }
                            setValue('cities', [...cities, option.id]);
                          }}
                        >
                          <Checkbox checked={value.indexOf(option.name) > -1} />
                          <ListItemText primary={option.name} />
                        </MenuItem>
                      ))
                    ) : (
                      <div>Loading</div>
                    )}
                  </Select>
                </>
              )}
            />
          </FormControl>

          <FormControl sx={{ width: '100%' }}>
            <Controller
              name="directions_value"
              control={control}
              rules={{ required: 'Направления обязательны' }}
              render={({ field: { value, onChange } }) => (
                <>
                  <InputLabel id="canals-label">Направления</InputLabel>
                  <Select
                    labelId="canals-label"
                    id="canals-multiple-checkbox"
                    multiple
                    sx={{ marginBottom: '24px' }}
                    value={value}
                    onChange={onChange}
                    fullWidth
                    input={<OutlinedInput label="Направления" />}
                    MenuProps={MenuProps}
                    renderValue={(selected) => selected?.map((option) => option).join(', ')}
                  >
                    {directions.data ? (
                      directions.data.map((item: any) => (
                        <MenuItem
                          key={item.id}
                          value={item.title}
                          onClick={() => {
                            const { directions } = getValues();
                            if (directions.includes(item.id)) {
                              const new_values = directions.filter((i) => i !== item.id);
                              setValue('directions', new_values);
                              return;
                            }
                            setValue('directions', [...directions, item.id]);
                          }}
                        >
                          <Checkbox checked={value.includes(item.title)} />
                          <ListItemText primary={item.title} />
                        </MenuItem>
                      ))
                    ) : (
                      <div>Loading</div>
                    )}
                  </Select>
                </>
              )}
            />
          </FormControl>

          <FormControl sx={{ width: '100%' }}>
            <Controller
              name="b2b_directions_value"
              control={control}
              rules={{ required: 'Каналы обязательны' }}
              render={({ field: { value, onChange } }) => (
                <>
                  <InputLabel id="canals-label">Каналы B2B</InputLabel>
                  <Select
                    labelId="canals-label"
                    id="canals-multiple-checkbox"
                    multiple
                    sx={{ marginBottom: '24px' }}
                    value={value}
                    onChange={onChange}
                    fullWidth
                    input={<OutlinedInput label="Каналы B2B" />}
                    MenuProps={MenuProps}
                    renderValue={(selected) => selected?.map((option) => option).join(', ')}
                  >
                    {constants.data && constants.data['SalesPoint.Direction'] ? (
                      Object.keys(constants.data['SalesPoint.Direction']).map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                          onClick={() => {
                            const dir = constants.data['SalesPoint.Direction'][option];
                            const { b2b_directions } = getValues();
                            if (b2b_directions.includes(dir)) {
                              const new_sources_ids = b2b_directions.filter((item) => item !== dir);
                              setValue('b2b_directions', new_sources_ids);
                              return;
                            }
                            setValue('b2b_directions', [...b2b_directions, dir]);
                          }}
                        >
                          <Checkbox checked={value.includes(option)} />
                          <ListItemText primary={option} />
                        </MenuItem>
                      ))
                    ) : (
                      <div>Loading</div>
                    )}
                  </Select>
                </>
              )}
            />
          </FormControl>

          <FormControl sx={{ width: '100%' }}>
            <Controller
              name="b2b_tiers_value"
              control={control}
              rules={{ required: 'Уровни обязательны' }}
              render={({ field: { value, onChange } }) => (
                <>
                  <InputLabel id="tiers-label">Уровень торговой точки</InputLabel>
                  <Select
                    labelId="tiers-label"
                    id="canals-multiple-checkbox"
                    multiple
                    sx={{ marginBottom: '24px' }}
                    value={value}
                    onChange={onChange}
                    fullWidth
                    input={<OutlinedInput label="Уровень торговой точки" />}
                    MenuProps={MenuProps}
                    renderValue={(selected) => selected?.map((option) => option).join(', ')}
                  >
                    {constants.data && constants.data['SalesPoint.Tier'] ? (
                      Object.keys(constants.data['SalesPoint.Tier']).map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                          onClick={() => {
                            const dir = constants.data['SalesPoint.Tier'][option];
                            const { b2b_tiers } = getValues();
                            if (b2b_tiers.includes(dir)) {
                              const new_sources_ids = b2b_tiers.filter((item) => item !== dir);
                              setValue('b2b_tiers', new_sources_ids);
                              return;
                            }
                            setValue('b2b_tiers', [...b2b_tiers, dir]);
                          }}
                        >
                          <Checkbox checked={value.includes(option)} />
                          <ListItemText primary={option} />
                        </MenuItem>
                      ))
                    ) : (
                      <div>Loading</div>
                    )}
                  </Select>
                </>
              )}
            />
          </FormControl>

          <Controller
            control={control}
            name="priority"
            render={({ field }) => (
              <TextField
                value={field.value}
                onChange={(e) => field.onChange(e)}
                label="Приоритет"
                fullWidth
                type="number"
                sx={{ marginBottom: '24px' }}
              />
            )}
          />

          {item && item.image && (
            <>
              <Typography>Текущее изображение</Typography>
              <Box
                component="img"
                sx={{ height: 250, width: '100%', objectFit: 'contain' }}
                alt={item.title}
                src={item.image}
              />
            </>
          )}

          <ImageUpload
            callback={uploadImage}
            isLoading={uploadProps.isLoading}
            isSuccess={uploadProps.isSuccess}
            silent
          />

          <Button
            onClick={onSendData}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            {item ? 'Изменить' : 'Создать'}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
